import * as React from 'react'
import { clsx } from 'clsx'
import { Plate, PlateContent } from 'platejs/react'
import type { PlateContentProps, PlateEditor, PlateProps } from 'platejs/react'

import { useMergeRefs } from '../../hooks/useMergeRefs'
import { useFormControlProps } from '../Form/useFormControlProps'
import { IconButton } from '../IconButton'
import { Toolbar } from '../Toolbar'

import styles from './RichTextField.module.css'

type RichTextFieldSize = 'small' | 'medium' | 'large'
type RichTextFieldVariant = 'embedded' | 'bordered'

type RichTextFieldContextValue = {
  disabled: boolean
  editableRef: React.RefObject<HTMLDivElement | null>
  readOnly: boolean
  size: RichTextFieldSize
}

const RichTextFieldContext =
  React.createContext<RichTextFieldContextValue | null>(null)

const useRichTextFieldContext = (): RichTextFieldContextValue => {
  const context = React.useContext(RichTextFieldContext)

  if (!context) {
    throw new Error(
      'RichTextField parts must be used within a RichTextField.Root.',
    )
  }

  return context
}

type RichTextFieldRootProps<TEditor extends PlateEditor = PlateEditor> = Omit<
  PlateProps<TEditor>,
  'children' | 'editor' | 'readOnly'
> &
  Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'children' | 'defaultValue' | keyof PlateProps<TEditor>
  > & {
    children: React.ReactNode
    disabled?: boolean
    editor: TEditor
    readOnly?: boolean
    size?: RichTextFieldSize
    variant?: RichTextFieldVariant
  }

type RichTextFieldRootComponent = {
  <TEditor extends PlateEditor>(
    props: RichTextFieldRootProps<TEditor> &
      React.RefAttributes<HTMLDivElement>,
  ): React.ReactElement | null
  displayName?: string
}

function RichTextFieldRootRender<TEditor extends PlateEditor>(
  {
    children,
    className,
    decorate,
    disabled = false,
    editor,
    onChange,
    onNodeChange,
    onSelectionChange,
    onTextChange,
    onValueChange,
    primary,
    readOnly = false,
    renderElement,
    renderLeaf,
    size = 'medium',
    suppressInstanceWarning,
    variant = 'bordered',
    ...props
  }: RichTextFieldRootProps<TEditor>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  const editableRef = React.useRef<HTMLDivElement>(null)
  const context = React.useMemo<RichTextFieldContextValue>(
    () => ({ disabled, editableRef, readOnly, size }),
    [disabled, readOnly, size],
  )

  return (
    <RichTextFieldContext.Provider value={context}>
      <div
        {...props}
        ref={ref}
        className={clsx(styles.root, styles[size], styles[variant], className)}
        data-disabled={disabled ? true : undefined}
        data-readonly={readOnly ? true : undefined}
        data-size={size}
        data-variant={variant}
      >
        <Plate
          decorate={decorate}
          editor={editor}
          onChange={onChange}
          onNodeChange={onNodeChange}
          onSelectionChange={onSelectionChange}
          onTextChange={onTextChange}
          onValueChange={onValueChange}
          primary={primary}
          readOnly={readOnly || disabled}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          suppressInstanceWarning={suppressInstanceWarning}
        >
          {children}
        </Plate>
      </div>
    </RichTextFieldContext.Provider>
  )
}

// React.forwardRef erases the editor subtype from the public callback types.
// oxlint-disable-next-line typescript/consistent-type-assertions -- The assertion restores the generic component signature.
const RichTextFieldRoot = React.forwardRef(
  RichTextFieldRootRender,
) as RichTextFieldRootComponent

RichTextFieldRoot.displayName = 'RichTextField.Root'

type RichTextFieldEditableProps = Omit<
  PlateContentProps,
  | 'aria-disabled'
  | 'aria-multiline'
  | 'aria-readonly'
  | 'disableDefaultStyles'
  | 'disabled'
  | 'readOnly'
  | 'role'
>

const RichTextFieldEditable = React.forwardRef<
  HTMLDivElement,
  RichTextFieldEditableProps
>(
  (
    {
      className,
      onBeforeInputCapture,
      onCutCapture,
      onDropCapture,
      onKeyDownCapture,
      onMouseDownCapture,
      onPasteCapture,
      onPointerDownCapture,
      onSelectCapture,
      tabIndex,
      ...props
    },
    ref,
  ) => {
    const { disabled, editableRef, readOnly } = useRichTextFieldContext()
    const controlProps = useFormControlProps(props)
    const mergedRef = useMergeRefs(ref, editableRef)

    const blockDisabledEvent = (event: React.SyntheticEvent): boolean => {
      if (!disabled) return false

      event.preventDefault()
      event.stopPropagation()
      return true
    }

    const handleKeyDownCapture: React.KeyboardEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (disabled && event.key !== 'Tab') {
        blockDisabledEvent(event)
        return
      }

      onKeyDownCapture?.(event)
    }

    const handleBeforeInputCapture: React.FormEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onBeforeInputCapture?.(event)
    }

    const handleCutCapture: React.ClipboardEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onCutCapture?.(event)
    }

    const handleDropCapture: React.DragEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onDropCapture?.(event)
    }

    const handleMouseDownCapture: React.MouseEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onMouseDownCapture?.(event)
    }

    const handlePasteCapture: React.ClipboardEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onPasteCapture?.(event)
    }

    const handlePointerDownCapture: React.PointerEventHandler<
      HTMLDivElement
    > = (event) => {
      if (!blockDisabledEvent(event)) onPointerDownCapture?.(event)
    }

    const handleSelectCapture: React.ReactEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (!blockDisabledEvent(event)) onSelectCapture?.(event)
    }

    return (
      <PlateContent
        {...controlProps}
        ref={mergedRef}
        aria-disabled={disabled ? true : undefined}
        aria-multiline="true"
        aria-readonly={readOnly ? true : undefined}
        className={clsx(styles.editable, className)}
        disableDefaultStyles
        disabled={disabled ? true : undefined}
        readOnly={readOnly || disabled}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- A rich text editor uses a contenteditable div with the textbox role.
        role="textbox"
        tabIndex={tabIndex ?? 0}
        onBeforeInputCapture={handleBeforeInputCapture}
        onCutCapture={handleCutCapture}
        onDropCapture={handleDropCapture}
        onKeyDownCapture={handleKeyDownCapture}
        onMouseDownCapture={handleMouseDownCapture}
        onPasteCapture={handlePasteCapture}
        onPointerDownCapture={handlePointerDownCapture}
        onSelectCapture={handleSelectCapture}
      />
    )
  },
)

RichTextFieldEditable.displayName = 'RichTextField.Editable'

type RichTextFieldFooterProps = React.ComponentPropsWithoutRef<'div'>

const RichTextFieldFooter = React.forwardRef<
  HTMLDivElement,
  RichTextFieldFooterProps
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx(styles.footer, className)} {...props} />
))

RichTextFieldFooter.displayName = 'RichTextField.Footer'

type RichTextFieldToolbarButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof IconButton>,
  'as' | 'asChild' | 'onMouseDown' | 'type'
>

const RichTextFieldToolbarButton = React.forwardRef<
  HTMLButtonElement,
  RichTextFieldToolbarButtonProps
>(({ disabled: disabledProp, ...props }, ref) => {
  const { disabled, readOnly } = useRichTextFieldContext()
  const isDisabled = disabled || readOnly || disabledProp

  return (
    <Toolbar.Button asChild disabled={isDisabled}>
      <IconButton
        variant="tertiary"
        {...props}
        ref={ref}
        disabled={isDisabled}
        type="button"
        onMouseDown={(event) => event.preventDefault()}
      />
    </Toolbar.Button>
  )
})

RichTextFieldToolbarButton.displayName = 'RichTextField.ToolbarButton'

const RichTextField = Object.assign(RichTextFieldRoot, {
  Root: RichTextFieldRoot,
  Editable: RichTextFieldEditable,
  Footer: RichTextFieldFooter,
  ToolbarButton: RichTextFieldToolbarButton,
})

export { RichTextField, useRichTextFieldContext }
export type {
  RichTextFieldEditableProps,
  RichTextFieldFooterProps,
  RichTextFieldRootProps,
  RichTextFieldSize,
  RichTextFieldToolbarButtonProps,
  RichTextFieldVariant,
}
